import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Layers, GraduationCap, LifeBuoy, ChevronRight, Scale } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { DocSearch } from './DocSearch';

interface DocSection {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  items: DocItem[];
}

interface DocItem {
  title: string;
  path: string;
}

const navigationSections: DocSection[] = [
  {
    title: 'Getting Started',
    icon: BookOpen,
    items: [
      { title: 'Documentation Home', path: '/docs' },
      { title: 'Getting Started Guide', path: '/docs/getting-started' }
    ]
  },
  {
    title: 'Features',
    icon: Layers,
    items: [
      { title: 'Features Overview', path: '/docs/features' },
      { title: 'Recall Map', path: '/docs/features/recall-map' },
      { title: 'Recall List & Search', path: '/docs/features/recall-list' },
      { title: 'Filters System', path: '/docs/features/filters' },
      { title: 'Manual Routing', path: '/docs/features/manual-routing' },
      { title: 'Route Preview', path: '/docs/features/route-preview' },
      { title: 'Route Tracker', path: '/docs/features/route-tracker' },
      { title: 'Dark Mode', path: '/docs/features/dark-mode' },
      { title: 'Campaign Management', path: '/docs/features/campaigns' },
      { title: 'Dealer Management', path: '/docs/features/dealers' },
      { title: 'Technician Management', path: '/docs/features/technicians' },
      { title: 'Profile & Settings', path: '/docs/features/profile' }
    ]
  },
  {
    title: 'Guides & Tutorials',
    icon: GraduationCap,
    items: [{ title: 'How-To Guides', path: '/docs/guides' }]
  },
  {
    title: 'Support',
    icon: LifeBuoy,
    items: [{ title: 'Support Policy', path: '/docs/support' }]
  },
  {
    title: 'Legal & Compliance',
    icon: Scale,
    items: [
      { title: 'Privacy Policy', path: '/docs/legal/privacy' },
      { title: 'Terms of Service', path: '/docs/legal/terms' },
      { title: 'Acceptable Use Policy', path: '/docs/legal/acceptable-use' },
      { title: 'Smart Features', path: '/docs/legal/smart-features' },
      { title: 'Cookie Policy', path: '/docs/legal/cookies' },
      { title: 'Data Processing', path: '/docs/legal/dpa' },
      { title: 'Service Providers', path: '/docs/legal/service-providers' },
      { title: 'Security', path: '/docs/legal/security' }
    ]
  }
];

interface DocsLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
}

export const DocsLayout: React.FC<DocsLayoutProps> = ({
  children,
  title,
  description,
  breadcrumbs
}) => {
  const location = useLocation();

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  // Handle hash navigation for anchor links
  useEffect(() => {
    if (location.hash) {
      // Remove the # from the hash
      const id = location.hash.substring(1);

      // Retry logic to handle delayed rendering
      const scrollToElement = (attempts = 0) => {
        const element = document.getElementById(id);

        if (element) {
          // Use setTimeout to ensure the page has fully rendered
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 0);
        } else if (attempts < 20) {
          // Retry for up to 2 seconds (20 attempts * 100ms)
          setTimeout(() => scrollToElement(attempts + 1), 100);
        }
      };

      // Start scrolling after a small delay to let the page render
      setTimeout(() => scrollToElement(), 100);
    } else {
      // If no hash, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  return (
    <div className='flex h-screen bg-background overflow-hidden'>
      {/* Left Sidebar Navigation - Fixed */}
      <aside className='hidden lg:flex lg:flex-col lg:w-64 lg:border-r border-border bg-muted/30 h-screen sticky top-0'>
        <div className='flex-1 py-6 px-4 overflow-y-auto'>
          <nav className='space-y-6'>
            {navigationSections.map(section => {
              const SectionIcon = section.icon;

              return (
                <div key={section.title}>
                  <h4 className='mb-3 text-sm font-semibold text-foreground flex items-center gap-2'>
                    {SectionIcon && <SectionIcon className='h-4 w-4' />}
                    <span>{section.title}</span>
                  </h4>
                  <ul className='space-y-1'>
                    {section.items.map(item => {
                      const isActive = isActivePath(item.path);

                      return (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                              isActive
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            <span>{item.title}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main Content Area - Scrollable */}
      <main className='flex-1 flex flex-col h-screen overflow-hidden'>
        {/* Fixed Search Bar */}
        <div className='sticky top-0 z-10 bg-background border-b border-border'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
            <DocSearch />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className='flex-1 overflow-y-auto'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className='flex items-center space-x-2 text-sm text-muted-foreground mb-6'>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <ChevronRight className='h-4 w-4' />}
                    {crumb.path ? (
                      <Link to={crumb.path} className='hover:text-foreground transition-colors'>
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className='text-foreground font-medium'>{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            )}

            {/* Page Header */}
            {(title || description) && (
              <div className='mb-8'>
                {title && (
                  <h1 className='text-4xl font-bold tracking-tight text-foreground mb-4'>
                    {title}
                  </h1>
                )}
                {description && <p className='text-lg text-muted-foreground'>{description}</p>}
                <Separator className='mt-6' />
              </div>
            )}

            {/* Page Content */}
            <div className='prose prose-slate dark:prose-invert max-w-none'>{children}</div>
          </div>
        </div>
      </main>
    </div>
  );
};
